import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, Plus, UserCircle, Bell, CheckCircle2, XCircle, Trash2, Edit2 } from 'lucide-react';
import { MOCK_USERS, MOCK_REQUESTS } from './data/mockData';
import { User, OOORequest } from './types';
import { Badge, Button } from './components/ui';
import { RequestFormModal } from './components/RequestFormModal';
import { ApprovalModal } from './components/ApprovalModal';
import { Dashboard } from './components/Dashboard';
import { AdminDashboard } from './components/AdminDashboard';
import { ActiveOutOfOffice } from './components/ActiveOutOfOffice';
import { useScreenInit } from './useScreenInit.js';
export function App() {
  // State
  const screenInit = useScreenInit();
  const initialUser = MOCK_USERS.find(u => u.id === screenInit?.currentUserId) || MOCK_USERS[0];
  const [currentUser, setCurrentUser] = useState<User>(initialUser); // Default to Manager
  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [requests, setRequests] = useState<OOORequest[]>(MOCK_REQUESTS);
  const [view, setView] = useState<'requests' | 'dashboard' | 'active'>(screenInit?.view === 'dashboard' || screenInit?.view === 'active' ? screenInit.view : 'requests');
  // Modals State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<OOORequest | null>(null);
  const [isApprovalOpen, setIsApprovalOpen] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState<OOORequest | null>(null);
  // Derived State
  const isAdmin = currentUser.role === 'Admin';
  const isManager = currentUser.role === 'Processing Manager' || currentUser.role === 'Loan Officer Manager';
  // The single requester role each manager is responsible for
  const managedRole = currentUser.role === 'Processing Manager' ? 'Processor' : currentUser.role === 'Loan Officer Manager' ? 'Loan Officer' : null;
  const visibleRequests = useMemo(() => {
    if (isAdmin) return requests;
    if (isManager) return requests.filter(r => r.userRole === managedRole);
    return requests.filter(r => r.userId === currentUser.id);
  }, [requests, currentUser, isAdmin, isManager, managedRole]);
  const pendingCount = visibleRequests.filter(r => r.status === 'Pending').length;
  // Handlers
  const handleCreateOrEdit = (data: Partial<OOORequest>) => {
    if (editingRequest) {
      setRequests(prev => prev.map(r => r.id === editingRequest.id ? {
        ...r,
        ...data
      } as OOORequest : r));
    } else {
      const newRequest: OOORequest = {
        id: `r${Date.now()}`,
        userId: currentUser.id,
        userName: currentUser.name,
        userRole: currentUser.role,
        status: 'Pending',
        startDate: data.startDate!,
        endDate: data.endDate!,
        message: data.message!
      };
      setRequests(prev => [newRequest, ...prev]);
    }
    setIsFormOpen(false);
    setEditingRequest(null);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      setRequests(prev => prev.filter(r => r.id !== id));
    }
  };
  const handleReject = (id: string) => {
    if (window.confirm('Are you sure you want to reject this request?')) {
      setRequests(prev => prev.map(r => r.id === id ? {
        ...r,
        status: 'Rejected'
      } : r));
    }
  };
  const handleApprove = (id: string, coverageIds: string[]) => {
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      status: 'Approved',
      coverageIds
    } : r));
    setIsApprovalOpen(false);
    setApprovingRequest(null);
  };
  const handleUpdateBalance = (userId: string, ptoTotal: number, ptoUsed: number) => {
    setUsers(prev => prev.map(u => u.id === userId ? {
      ...u,
      ptoTotal,
      ptoUsed
    } : u));
  };
  const openEdit = (request: OOORequest) => {
    setEditingRequest(request);
    setIsFormOpen(true);
  };
  const openApprove = (request: OOORequest) => {
    setApprovingRequest(request);
    setIsApprovalOpen(true);
  };
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };
  return <div className="min-h-screen bg-[#f3f2f2] font-sans text-gray-900" data-id="element-0">
      {/* Header / Navigation */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center" data-id="element-3">
            <div className="bg-[#0176d3] p-2 rounded mr-3" data-id="element-4">
              <Calendar className="w-5 h-5 text-white" data-id="element-5" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 tracking-tight" data-id="element-6">
              Out of Office Management
            </h1>
          </div>

          <div className="flex items-center space-x-6" data-id="element-7">
            {(isManager || isAdmin) && pendingCount > 0 && <div className="flex items-center text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full text-sm font-medium border border-amber-200" data-id="element-8">
                <Bell className="w-4 h-4 mr-2" data-id="element-9" />
                {pendingCount} Pending Request{pendingCount !== 1 ? 's' : ''}
              </div>}

            <div className="flex items-center space-x-3 border-l border-gray-200 pl-6" data-id="element-10">
              <UserCircle className="w-8 h-8 text-gray-400" data-id="element-11" />
              <div className="flex flex-col" data-id="element-12">
                <select className="text-sm font-medium text-gray-900 bg-transparent border-none focus:ring-0 p-0 cursor-pointer" value={currentUser.id} onChange={e => setCurrentUser(users.find(u => u.id === e.target.value)!)} data-id="element-13">
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-14">
                      {u.name} ({u.role})
                    </option>)}
                </select>
                <span className="text-xs text-gray-500" data-id="element-15">
                  {currentUser.role} View
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Navigation / Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex space-x-8" data-id="element-16">
          <button onClick={() => setView('requests')} className={`py-3 border-b-2 font-medium text-sm transition-colors ${view === 'requests' ? 'border-[#0176d3] text-[#0176d3]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-17">
            Requests
          </button>
          <button onClick={() => setView('dashboard')} className={`py-3 border-b-2 font-medium text-sm transition-colors ${view === 'dashboard' ? 'border-[#0176d3] text-[#0176d3]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-18">
            Dashboard
          </button>
          {(isManager || isAdmin) && <button onClick={() => setView('active')} className={`py-3 border-b-2 font-medium text-sm transition-colors ${view === 'active' ? 'border-[#0176d3] text-[#0176d3]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19">
              Active OOO
            </button>}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-20">
        {view === 'dashboard' ? isAdmin ? <AdminDashboard allUsers={users} allRequests={requests} onUpdateBalance={handleUpdateBalance} data-id="element-21" /> : <Dashboard currentUser={currentUser} managedRole={managedRole} allUsers={users} allRequests={requests} onUpdateBalance={isManager ? handleUpdateBalance : undefined} data-id="element-22" /> : view === 'active' && (isManager || isAdmin) ? <ActiveOutOfOffice managedRole={managedRole} requests={requests} users={users} data-id="element-23" /> : <>
            <div className="flex items-center justify-between mb-6" data-id="element-24">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-25">
                {isAdmin ? 'All Org Requests' : isManager ? `${managedRole} Requests` : 'My Requests'}
              </h2>
              {!isManager && !isAdmin && <Button variant="brand" onClick={() => {
            setEditingRequest(null);
            setIsFormOpen(true);
          }} data-id="element-26">
                  <Plus className="w-4 h-4 mr-2" data-id="element-27" />
                  New Request
                </Button>}
            </div>

            {/* Requests List */}
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-28">
              {visibleRequests.length === 0 ? <div className="p-12 text-center" data-id="element-29">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-30" />
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-31">
                    No requests found
                  </h3>
                  <p className="text-gray-500 mt-1" data-id="element-32">
                    {isManager ? 'There are no out of office requests to review.' : "You haven't submitted any out of office requests yet."}
                  </p>
                </div> : <ul className="divide-y divide-gray-200" data-id="element-33">
                  {visibleRequests.map(request => <li key={request.id} className="p-6 hover:bg-gray-50 transition-colors" data-id="element-34">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-35">
                        {/* Left Column: Details */}
                        <div className="flex-1 space-y-3" data-id="element-36">
                          <div className="flex items-center space-x-3" data-id="element-37">
                            <h3 className="text-lg font-semibold text-gray-900" data-id="element-38">
                              {request.userName}
                            </h3>
                            {isManager && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-39">
                                {request.userRole}
                              </span>}
                            <Badge status={request.status} data-id="element-40" />
                          </div>

                          <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-41">
                            <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-42">
                              <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-43" />
                              <span className="font-medium" data-id="element-44">
                                {formatDate(request.startDate)}
                              </span>
                              <span className="mx-2 text-gray-400" data-id="element-45">→</span>
                              <span className="font-medium" data-id="element-46">
                                {formatDate(request.endDate)}
                              </span>
                            </div>
                          </div>

                          <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-47">
                            {request.message}
                          </div>

                          {/* Coverage Info (if approved) */}
                          {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600 flex items-center mt-2" data-id="element-48">
                                <span className="font-medium mr-2" data-id="element-49">
                                  Coverage assigned:
                                </span>
                                {request.coverageIds.map(id => users.find(u => u.id === id)?.name).join(', ')}
                              </div>}
                        </div>

                        {/* Right Column: Actions */}
                        {!isAdmin && <div className="flex items-center space-x-2 md:ml-6 shrink-0" data-id="element-50">
                            {isManager ? request.status === 'Pending' ? <>
                                  <Button variant="brand" onClick={() => openApprove(request)} data-id="element-51">
                                    <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-52" />
                                    Approve
                                  </Button>
                                  <Button variant="destructive" onClick={() => handleReject(request.id)} data-id="element-53">
                                    <XCircle className="w-4 h-4 mr-2" data-id="element-54" />
                                    Reject
                                  </Button>
                                </> : null : request.status === 'Pending' ? <>
                                <Button variant="neutral" onClick={() => openEdit(request)} data-id="element-55">
                                  <Edit2 className="w-4 h-4 mr-2" data-id="element-56" />
                                  Edit
                                </Button>
                                <Button variant="destructive" onClick={() => handleDelete(request.id)} data-id="element-57">
                                  <Trash2 className="w-4 h-4 mr-2" data-id="element-58" />
                                  Delete
                                </Button>
                              </> : null}
                          </div>}
                      </div>
                    </li>)}
                </ul>}
            </div>
          </>}
      </main>

      {/* Modals */}
      <RequestFormModal isOpen={isFormOpen} onClose={() => {
      setIsFormOpen(false);
      setEditingRequest(null);
    }} onSubmit={handleCreateOrEdit} initialData={editingRequest} data-id="element-59" />

      <ApprovalModal isOpen={isApprovalOpen} onClose={() => {
      setIsApprovalOpen(false);
      setApprovingRequest(null);
    }} onApprove={handleApprove} request={approvingRequest} availableUsers={users} data-id="element-60" />
    </div>;
}